import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";
import Next_Front_Favicon from "./Next_Front_Favicon.png";

// ─── EMAILJS CONFIGURATION ──────────────────────────────────────────────────
const EMAILJS_CONFIG = {
  SERVICE_ID: "service_somu38y",
  TEMPLATE_ID: "template_jvsn8tq",
  PUBLIC_KEY: "nbgiKhgvRrvIMJUfQ",
};

// ─── ICON CLOSE ─────────────────────────────────────────────────────────────
const IconClose = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // ─── CONTACT MODAL STATE ──────────────────────────────────────────────────
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactStep, setContactStep] = useState(1);
  const [contactInterest, setContactInterest] = useState("");
  const [contactBeds, setContactBeds] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState("");
  const [formData, setFormData] = useState({ 
    firstName: "", 
    lastName: "", 
    email: "", 
    phone: "", 
    message: "",
    interest: "",
    bedrooms: ""
  });

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Check if link is active
  const isActive = (path) => location.pathname === path;

  // ─── CONTACT FORM FUNCTIONS ──────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName?.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Please enter a valid phone number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitContact = async () => {
    if (!validateForm()) {
      setToast("❌ Please fill in all required fields");
      setTimeout(() => setToast(""), 2800);
      return;
    }

    setIsSubmitting(true);

    try {
      const templateParams = {
        interest: contactInterest || "General Inquiry",
        from_name: `${formData.firstName} ${formData.lastName}`.trim(),
        from_email: formData.email.trim(),
        from_phone: formData.phone.trim(),
        bedrooms: contactBeds || "Not specified",
        message: formData.message?.trim() || "No additional message provided",
        date: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      console.log("EmailJS success:", response);
      setSubmitted(true);
      setToast("✅ Thank you! Your request has been submitted successfully.");

      setTimeout(() => {
        setFormData({ firstName: "", lastName: "", email: "", phone: "", message: "", interest: "", bedrooms: "" });
        setErrors({});
        setContactStep(1);
        setContactInterest("");
        setContactBeds("");
        setContactModalOpen(false);
        setSubmitted(false);
        setTimeout(() => setToast(""), 2000);
      }, 1500);

    } catch (error) {
      console.error("EmailJS error:", error);
      setIsSubmitting(false);

      const useMailto = window.confirm(
        "❌ Email service is temporarily unavailable.\n\n" +
        "Would you like to send your inquiry directly via your email app instead?\n\n" +
        "✓ Click OK to open your email app with all details pre-filled\n" +
        "✓ Click Cancel to close and try again later"
      );

      if (useMailto) {
        const subject = `Property Inquiry: ${contactInterest || "New Inquiry"} - ${formData.firstName} ${formData.lastName}`;
        const body = `
NEXT.FRONT PROPERTIES - NEW INQUIRY
====================================
Interest: ${contactInterest || "General Inquiry"}
Bedrooms: ${contactBeds || "Not specified"}

CONTACT DETAILS
------------------------------------
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone}

MESSAGE
------------------------------------
${formData.message || "No message provided"}

Submitted: ${new Date().toLocaleString()}
        `.trim();
        window.location.href = `mailto:info@nextfrontproperties.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setToast("📧 Opening your email app...");
        setTimeout(() => setToast(""), 2800);
        setContactModalOpen(false);
      } else {
        setToast("❌ Submission cancelled. Please try again later.");
        setTimeout(() => setToast(""), 2800);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openContactModal = () => {
    setContactModalOpen(true);
    setContactStep(1);
    setSubmitted(false);
    setErrors({});
    document.body.style.overflow = "hidden";
  };

  const closeContactModal = () => {
    setContactModalOpen(false);
    document.body.style.overflow = "unset";
    setTimeout(() => {
      if (!submitted) {
        setFormData({ firstName: "", lastName: "", email: "", phone: "", message: "", interest: "", bedrooms: "" });
        setErrors({});
        setContactStep(1);
        setContactInterest("");
        setContactBeds("");
      }
    }, 400);
  };

  const handleInterestSelect = (value) => {
    setContactInterest(value);
    setContactStep(2);
  };

  const handleBedSelect = (value) => {
    setContactBeds(value);
    setContactStep(3);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');

        /* Base Navbar Styles */
        .sr-nav {
          position: fixed; 
          top: 0; 
          left: 0; 
          right: 0; 
          z-index: 1000;
          display: flex; 
          align-items: center; 
          justify-content: space-between;
          padding: 0 56px; 
          height: 80px;
          transition: all 0.4s ease;
        }
        
        .sr-nav.transparent {
          background: linear-gradient(to bottom, rgba(7,16,30,0.95) 0%, rgba(7,16,30,0.7) 100%);
          border-bottom: 1px solid rgba(212,175,55,0.1);
        }
        
        .sr-nav.scrolled {
          background: rgba(7,16,30,0.98);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(212,175,55,0.15);
          box-shadow: 0 4px 30px rgba(0,0,0,0.3);
          height: 70px;
        }

        /* Logo Styles */
        .sr-nav-logo {
          font-family: 'Playfair Display', serif;
          font-size: 24px; 
          font-weight: 700;
          text-decoration: none; 
          display: flex; 
          align-items: center; 
          gap: 12px;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        
        .sr-nav-logo:hover {
          transform: scale(1.02);
        }
        
        .sr-nav-logo-icon {
          width: 45px; 
          height: 45px;
          border: 2px solid #D4AF37;
          display: flex; 
          align-items: center; 
          justify-content: center;
          overflow: hidden;
          border-radius: 12px;
          background: linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%);
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        
        .sr-nav-logo-icon img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .sr-nav-logo:hover .sr-nav-logo-icon {
          border-radius: 16px;
          background: linear-gradient(135deg, rgba(212,175,55,0.25) 0%, rgba(212,175,55,0.1) 100%);
          transform: rotate(5deg) scale(1.05);
        }
        
        .sr-nav-logo-text {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }
        
        .sr-nav-logo-main {
          font-size: 24px;
          font-weight: 800;
          background: linear-gradient(135deg, #D4AF37 0%, #F3E5AB 25%, #FFD700 50%, #D4AF37 75%, #C9A84C 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: shimmer 3s linear infinite;
          letter-spacing: 0.5px;
        }
        
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .sr-nav-logo-sub {
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 3px;
          background: linear-gradient(90deg, #D4AF37 0%, #FFD700 50%, #F3E5AB 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          text-transform: uppercase;
          margin-top: 2px;
          position: relative;
          display: inline-block;
        }
        
        .sr-nav-logo-sub::before {
          content: '';
          position: absolute;
          bottom: -3px;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }
        
        .sr-nav-logo:hover .sr-nav-logo-sub::before {
          transform: scaleX(1);
        }

        /* Navigation Links */
        .sr-nav-links {
          display: flex; 
          gap: 42px; 
          list-style: none; 
          margin: 0; 
          padding: 0;
          align-items: center;
        }
        
        .sr-nav-links li {
          position: relative;
        }
        
        .sr-nav-links a {
          color: rgba(232,224,208,0.85); 
          font-size: 14px; 
          font-weight: 500;
          text-decoration: none; 
          letter-spacing: 0.8px;
          font-family: 'DM Sans', sans-serif;
          transition: color 0.3s ease; 
          position: relative; 
          padding: 8px 0;
          display: inline-block;
          white-space: nowrap;
        }
        
        .sr-nav-links a::after {
          content: ''; 
          position: absolute; 
          bottom: 0; 
          left: 50%;
          right: 50%;
          height: 2px; 
          background: linear-gradient(90deg, #D4AF37, #FFD700);
          transition: all 0.3s ease;
          transform: translateX(-50%);
        }
        
        .sr-nav-links a:hover { 
          color: #FFD700; 
        }
        
        .sr-nav-links a:hover::after {
          left: 0;
          right: 0;
          width: 100%;
        }

        /* Active link style */
        .sr-nav-links a.active {
          color: #FFD700;
        }
        
        .sr-nav-links a.active::after {
          left: 0;
          right: 0;
          width: 100%;
        }

        /* Right Section */
        .sr-nav-right { 
          display: flex; 
          align-items: center; 
          gap: 28px; 
          flex-shrink: 0;
        }

        .sr-nav-phone {
          display: flex; 
          align-items: center; 
          gap: 10px;
          font-family: 'DM Sans', sans-serif; 
          font-size: 14px;
          font-weight: 600;
          color: rgba(232,224,208,0.85); 
          text-decoration: none;
          transition: all 0.3s ease;
          padding: 8px 18px;
          border-radius: 30px;
          background: linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0.05) 100%);
          border: 1px solid rgba(212,175,55,0.2);
          white-space: nowrap;
        }
        
        .sr-nav-phone:hover { 
          color: #FFD700;
          background: linear-gradient(135deg, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0.1) 100%);
          border-color: rgba(212,175,55,0.4);
          transform: translateY(-2px);
        }
        
        .sr-nav-phone svg {
          transition: transform 0.3s ease;
          stroke: #D4AF37;
          flex-shrink: 0;
        }
        
        .sr-nav-phone:hover svg {
          transform: scale(1.1);
        }

        .sr-nav-cta {
          background: linear-gradient(135deg, #D4AF37 0%, #C9A84C 25%, #B8962E 50%, #C9A84C 75%, #D4AF37 100%);
          background-size: 200% auto;
          color: #08111f; 
          border: none;
          padding: 12px 28px; 
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; 
          font-weight: 700; 
          letter-spacing: 1.2px;
          text-transform: uppercase; 
          cursor: pointer; 
          transition: all 0.3s ease;
          border-radius: 30px;
          box-shadow: 0 2px 15px rgba(212,175,55,0.3);
          animation: btnShimmer 2s ease infinite;
          white-space: nowrap;
        }
        
        @keyframes btnShimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .sr-nav-cta:hover { 
          background: linear-gradient(135deg, #F3E5AB 0%, #D4AF37 50%, #F3E5AB 100%);
          background-size: 200% auto;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(212,175,55,0.4);
        }

        /* Hamburger Menu */
        .sr-nav-hamburger {
          display: none; 
          flex-direction: column; 
          gap: 6px;
          background: rgba(212,175,55,0.1); 
          border: 1px solid rgba(212,175,55,0.2);
          cursor: pointer; 
          padding: 10px 12px;
          border-radius: 10px;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        
        .sr-nav-hamburger:hover {
          background: rgba(212,175,55,0.2);
          border-color: rgba(212,175,55,0.4);
        }
        
        .sr-nav-hamburger span {
          display: block; 
          width: 24px; 
          height: 2px; 
          background: linear-gradient(90deg, #D4AF37, #FFD700);
          transition: all 0.3s ease;
          border-radius: 2px;
        }

        /* Mobile Menu */
        .sr-mobile-menu {
          display: none; 
          position: fixed; 
          top: 80px; 
          left: 0; 
          right: 0;
          background: rgba(7,16,30,0.98); 
          backdrop-filter: blur(20px);
          padding: 32px 28px; 
          border-bottom: 1px solid rgba(212,175,55,0.15);
          z-index: 999;
          animation: slideDown 0.3s ease;
          max-height: calc(100vh - 80px);
          overflow-y: auto;
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .sr-mobile-menu.open { display: block; }
        
        .sr-mobile-menu ul { 
          list-style: none; 
          margin: 0; 
          padding: 0; 
        }
        
        .sr-mobile-menu ul li { 
          border-bottom: 1px solid rgba(255,255,255,0.05);
          opacity: 0;
          animation: fadeInUp 0.3s ease forwards;
        }
        
        .sr-mobile-menu ul li:nth-child(1) { animation-delay: 0.05s; }
        .sr-mobile-menu ul li:nth-child(2) { animation-delay: 0.1s; }
        .sr-mobile-menu ul li:nth-child(3) { animation-delay: 0.15s; }
        .sr-mobile-menu ul li:nth-child(4) { animation-delay: 0.2s; }
        .sr-mobile-menu ul li:nth-child(5) { animation-delay: 0.25s; }
        .sr-mobile-menu ul li:nth-child(6) { animation-delay: 0.3s; }
        .sr-mobile-menu ul li:nth-child(7) { animation-delay: 0.35s; }
        .sr-mobile-menu ul li:nth-child(8) { animation-delay: 0.4s; }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .sr-mobile-menu ul li a {
          display: block; 
          padding: 16px 0;
          font-family: 'DM Sans', sans-serif; 
          font-size: 16px;
          color: #e8e0d0; 
          text-decoration: none; 
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .sr-mobile-menu ul li a:hover {
          color: #FFD700;
          padding-left: 12px;
          background: linear-gradient(90deg, rgba(212,175,55,0.1) 0%, transparent 100%);
        }
        
        .sr-mobile-menu ul li a.active-mobile {
          color: #FFD700;
          padding-left: 12px;
          background: linear-gradient(90deg, rgba(212,175,55,0.15) 0%, transparent 100%);
        }
        
        .sr-mobile-cta {
          display: block; 
          width: 100%; 
          margin-top: 24px;
          background: linear-gradient(135deg, #D4AF37 0%, #C9A84C 50%, #D4AF37 100%);
          background-size: 200% auto;
          color: #08111f; 
          border: none;
          padding: 16px; 
          font-family: 'DM Sans', sans-serif;
          font-size: 15px; 
          font-weight: 700; 
          letter-spacing: 1.5px;
          text-transform: uppercase; 
          cursor: pointer;
          border-radius: 30px;
          transition: all 0.3s ease;
          animation: btnShimmer 2s ease infinite;
        }
        
        .sr-mobile-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(212,175,55,0.4);
        }

        /* ─── CONTACT MODAL STYLES ── */
        .ke-contact-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.92);
          backdrop-filter: blur(12px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .ke-contact-modal-content {
          background: linear-gradient(145deg, #0d1a2d 0%, #07101e 100%);
          border: 1px solid rgba(201,168,76,0.2);
          border-radius: 16px;
          max-width: 560px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
          padding: 40px 36px;
        }

        .ke-contact-modal-content::-webkit-scrollbar {
          width: 4px;
        }
        .ke-contact-modal-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .ke-contact-modal-content::-webkit-scrollbar-thumb {
          background: #C9A84C;
          border-radius: 2px;
        }

        .ke-contact-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
        }

        .ke-contact-modal-close:hover {
          background: rgba(201,168,76,0.2);
          border-color: #C9A84C;
          transform: rotate(90deg);
        }

        .ke-contact-modal-close svg {
          width: 20px;
          height: 20px;
        }

        .ke-contact-steps {
          display: flex;
          gap: 8px;
          margin-bottom: 28px;
          justify-content: center;
        }

        .ke-contact-step-dot {
          width: 30px;
          height: 3px;
          border-radius: 2px;
          background: rgba(201,168,76,0.14);
          transition: background 0.3s;
        }

        .ke-contact-step-dot.active {
          background: #C9A84C;
        }

        .ke-toast {
          position: fixed;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          background: #0d1a2d;
          border: 1px solid #C9A84C;
          color: #e8e0d0;
          padding: 14px 28px;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.5px;
          z-index: 99999;
          display: flex;
          align-items: center;
          gap: 10px;
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
          animation: toastIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          backdrop-filter: blur(10px);
        }

        @keyframes toastIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0) scale(1);
          }
        }

        @media (max-width: 480px) {
          .ke-toast {
            font-size: 12px;
            padding: 12px 20px;
            bottom: 24px;
            white-space: normal;
            max-width: 90%;
            text-align: center;
          }
          .ke-contact-modal-content {
            padding: 28px 20px;
            margin: 10px;
          }
          .ke-contact-modal-content .ke-contact-steps {
            gap: 6px;
          }
          .ke-contact-modal-content .ke-contact-step-dot {
            width: 24px;
          }
        }

        /* ============================================= */
        /* RESPONSIVE BREAKPOINTS */
        /* ============================================= */

        /* Large Desktops (1200px - 1400px) */
        @media (max-width: 1400px) {
          .sr-nav-links { gap: 32px; }
          .sr-nav { padding: 0 40px; }
        }

        /* Medium Desktops (1100px - 1200px) */
        @media (max-width: 1200px) {
          .sr-nav-links { gap: 24px; }
          .sr-nav-links a { font-size: 13px; letter-spacing: 0.6px; }
          .sr-nav-phone { font-size: 13px; padding: 6px 14px; }
          .sr-nav-cta { font-size: 12px; padding: 10px 22px; }
          .sr-nav-right { gap: 20px; }
        }

        /* Small Desktops / Tablets Landscape (992px - 1100px) */
        @media (max-width: 1100px) {
          .sr-nav-links { gap: 20px; }
          .sr-nav { padding: 0 32px; }
          .sr-nav-links a { font-size: 12px; letter-spacing: 0.4px; }
          .sr-nav-phone { font-size: 12px; padding: 6px 12px; gap: 6px; }
          .sr-nav-phone svg { width: 14px; height: 14px; }
          .sr-nav-cta { font-size: 11px; padding: 8px 18px; letter-spacing: 1px; }
          .sr-nav-right { gap: 16px; }
        }

        /* Tablets (768px - 992px) - Show Hamburger, Hide Links and Phone */
        @media (max-width: 992px) {
          .sr-nav-links { display: none; }
          .sr-nav-phone { display: none; }
          .sr-nav-hamburger { display: flex; }
          .sr-nav { padding: 0 24px; height: 72px; }
          .sr-nav.scrolled { height: 64px; }
          .sr-nav-cta { font-size: 11px; padding: 8px 16px; }
          .sr-nav-right { gap: 12px; }
          .sr-mobile-menu { top: 72px; }
          
          .sr-nav.scrolled + .sr-mobile-menu {
            top: 64px;
          }
        }

        /* Small Tablets / Large Phones (576px - 768px) */
        @media (max-width: 768px) {
          .sr-nav { padding: 0 16px; height: 68px; }
          .sr-nav.scrolled { height: 60px; }
          .sr-nav-logo { gap: 8px; }
          .sr-nav-logo-icon { width: 38px; height: 38px; border-radius: 10px; }
          .sr-nav-logo-main { font-size: 20px; }
          .sr-nav-logo-sub { font-size: 8px; letter-spacing: 2.5px; }
          .sr-nav-cta { 
            font-size: 10px; 
            padding: 6px 14px; 
            letter-spacing: 0.8px;
          }
          .sr-nav-hamburger { padding: 8px 10px; gap: 5px; }
          .sr-nav-hamburger span { width: 20px; height: 2px; }
          .sr-mobile-menu { 
            top: 68px; 
            padding: 24px 20px; 
          }
          .sr-nav.scrolled + .sr-mobile-menu {
            top: 60px;
          }
          .sr-mobile-menu ul li a { 
            font-size: 15px; 
            padding: 14px 0; 
          }
          .sr-mobile-cta { 
            font-size: 14px; 
            padding: 14px; 
          }
        }

        /* Mobile Phones (480px - 576px) */
        @media (max-width: 576px) {
          .sr-nav { padding: 0 12px; height: 64px; }
          .sr-nav.scrolled { height: 58px; }
          .sr-nav-logo { gap: 6px; }
          .sr-nav-logo-icon { width: 34px; height: 34px; border-radius: 8px; border-width: 1.5px; }
          .sr-nav-logo-main { font-size: 18px; }
          .sr-nav-logo-sub { font-size: 7px; letter-spacing: 2px; }
          .sr-nav-cta { 
            font-size: 9px; 
            padding: 5px 12px; 
            letter-spacing: 0.6px;
            border-radius: 20px;
          }
          .sr-nav-hamburger { padding: 6px 8px; gap: 4px; border-radius: 8px; }
          .sr-nav-hamburger span { width: 18px; height: 1.5px; }
          .sr-nav-right { gap: 8px; }
          .sr-mobile-menu { 
            top: 64px; 
            padding: 20px 16px; 
          }
          .sr-nav.scrolled + .sr-mobile-menu {
            top: 58px;
          }
          .sr-mobile-menu ul li a { 
            font-size: 14px; 
            padding: 12px 0; 
          }
          .sr-mobile-cta { 
            font-size: 13px; 
            padding: 12px; 
            letter-spacing: 1.2px;
          }
        }

        /* Small Mobile Phones (320px - 480px) */
        @media (max-width: 480px) {
          .sr-nav { padding: 0 10px; height: 60px; }
          .sr-nav.scrolled { height: 54px; }
          .sr-nav-logo { gap: 4px; }
          .sr-nav-logo-icon { width: 30px; height: 30px; border-radius: 6px; border-width: 1.5px; }
          .sr-nav-logo-main { font-size: 16px; }
          .sr-nav-logo-sub { font-size: 6px; letter-spacing: 1.5px; }
          .sr-nav-cta { 
            font-size: 8px; 
            padding: 4px 10px; 
            letter-spacing: 0.4px;
            border-radius: 16px;
          }
          .sr-nav-hamburger { padding: 5px 7px; gap: 3px; border-radius: 6px; }
          .sr-nav-hamburger span { width: 16px; height: 1.5px; }
          .sr-nav-right { gap: 6px; }
          .sr-mobile-menu { 
            top: 60px; 
            padding: 16px 12px; 
          }
          .sr-nav.scrolled + .sr-mobile-menu {
            top: 54px;
          }
          .sr-mobile-menu ul li a { 
            font-size: 13px; 
            padding: 10px 0; 
          }
          .sr-mobile-cta { 
            font-size: 12px; 
            padding: 10px; 
            letter-spacing: 1px;
            border-radius: 20px;
          }
        }

        /* Extra Small Devices (below 320px) */
        @media (max-width: 360px) {
          .sr-nav { padding: 0 8px; height: 56px; }
          .sr-nav.scrolled { height: 50px; }
          .sr-nav-logo-icon { width: 26px; height: 26px; border-radius: 5px; }
          .sr-nav-logo-main { font-size: 14px; }
          .sr-nav-logo-sub { font-size: 5px; letter-spacing: 1px; }
          .sr-nav-cta { 
            font-size: 7px; 
            padding: 3px 8px; 
            letter-spacing: 0.3px;
          }
          .sr-nav-hamburger { padding: 4px 6px; gap: 3px; }
          .sr-nav-hamburger span { width: 14px; height: 1.5px; }
          .sr-mobile-menu { 
            top: 56px; 
            padding: 12px 10px; 
          }
          .sr-nav.scrolled + .sr-mobile-menu {
            top: 50px;
          }
          .sr-mobile-menu ul li a { 
            font-size: 12px; 
            padding: 8px 0; 
          }
          .sr-mobile-cta { 
            font-size: 11px; 
            padding: 8px; 
            letter-spacing: 0.8px;
          }
        }

        /* Landscape Phones - Adjust height */
        @media (max-height: 500px) and (orientation: landscape) {
          .sr-nav { height: 56px; }
          .sr-nav.scrolled { height: 50px; }
          .sr-nav-logo-icon { width: 30px; height: 30px; }
          .sr-nav-logo-main { font-size: 16px; }
          .sr-nav-logo-sub { font-size: 6px; }
          .sr-mobile-menu { 
            top: 56px; 
            padding: 16px; 
            max-height: calc(100vh - 56px);
          }
          .sr-mobile-menu ul li a { 
            padding: 10px 0; 
            font-size: 14px; 
          }
        }

        /* Print Styles */
        @media print {
          .sr-nav {
            position: static;
            background: #fff !important;
            border-bottom: 1px solid #ddd;
          }
          .sr-nav-cta,
          .sr-nav-hamburger,
          .sr-nav-phone,
          .sr-mobile-menu {
            display: none !important;
          }
          .sr-nav-links {
            display: flex !important;
          }
          .sr-nav-links a {
            color: #333 !important;
          }
        }

        /* High DPI / Retina Screens */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .sr-nav-logo-icon {
            border-width: 1.5px;
          }
        }

        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .sr-nav.transparent {
            background: linear-gradient(to bottom, rgba(7,16,30,0.98) 0%, rgba(7,16,30,0.8) 100%);
          }
        }

        /* Reduced Motion Preferences */
        @media (prefers-reduced-motion: reduce) {
          .sr-nav,
          .sr-nav-logo,
          .sr-nav-logo-icon,
          .sr-nav-cta,
          .sr-nav-phone {
            animation: none !important;
            transition: none !important;
          }
          .sr-nav-logo-main {
            animation: none !important;
          }
          .sr-mobile-menu {
            animation: none !important;
          }
          .sr-mobile-menu ul li {
            animation: none !important;
            opacity: 1 !important;
          }
        }
      `}</style>

      <nav className={`sr-nav ${scrolled ? "scrolled" : "transparent"}`}>
        <Link to="/" className="sr-nav-logo">
          <div className="sr-nav-logo-icon">
            <img src={Next_Front_Favicon} alt="NextFront Properties" />
          </div>
          <div className="sr-nav-logo-text">
            <span className="sr-nav-logo-main">NextFront</span>
            <span className="sr-nav-logo-sub">PROPERTIES</span>
          </div>
        </Link>

        <ul className="sr-nav-links">
          <li><Link to="/" className={isActive("/") ? "active" : ""}>Home</Link></li>
          <li><Link to="/about" className={isActive("/about") ? "active" : ""}>About Us</Link></li>
          <li><a href="#">Projects</a></li>
          <li><Link to="/dubai" className={isActive("/dubai") ? "active" : ""}>Dubai Properties</Link></li>
          <li><a href="#">Localities</a></li>
          <li><a href="#">Blog</a></li>
          <li><a href="#">FAQ</a></li>
        </ul>

        <div className="sr-nav-right">
          <a href="tel:+918584826222" className="sr-nav-phone">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.53 2 2 0 0 1 3.56 1.35h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/>
            </svg>
            +91 85848 26222
          </a>
          <button className="sr-nav-cta" onClick={openContactModal}>Contact Us</button>
          <button className="sr-nav-hamburger" onClick={() => setMenuOpen(o => !o)}>
            <span style={{ transform: menuOpen ? "rotate(45deg) translateY(10px)" : "none" }} />
            <span style={{ opacity: menuOpen ? 0 : 1, width: menuOpen ? "0" : "20px" }} />
            <span style={{ transform: menuOpen ? "rotate(-45deg) translateY(-10px)" : "none" }} />
          </button>
        </div>
      </nav>

      <div className={`sr-mobile-menu ${menuOpen ? "open" : ""}`}>
        <ul>
          <li><Link to="/" onClick={() => setMenuOpen(false)} className={isActive("/") ? "active-mobile" : ""}>Home</Link></li>
          <li><Link to="/about" onClick={() => setMenuOpen(false)} className={isActive("/about") ? "active-mobile" : ""}>About Us</Link></li>
          <li><a href="#" onClick={() => setMenuOpen(false)}>Projects</a></li>
          <li><Link to="/dubai" onClick={() => setMenuOpen(false)} className={isActive("/dubai") ? "active-mobile" : ""}>Dubai Properties</Link></li>
          <li><a href="#" onClick={() => setMenuOpen(false)}>Localities</a></li>
          <li><a href="#" onClick={() => setMenuOpen(false)}>Blog</a></li>
          <li><a href="#" onClick={() => setMenuOpen(false)}>FAQ</a></li>
        </ul>
        <a href="tel:+918584826222" style={{ textDecoration: 'none' }}>
          <button className="sr-mobile-cta">📞 Call Now: +91 85848 26222</button>
        </a>
      </div>

      {/* ─── CONTACT MODAL ── */}
      {contactModalOpen && (
        <div className="ke-contact-modal-overlay" onClick={closeContactModal}>
          <div className="ke-contact-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="ke-contact-modal-close" onClick={closeContactModal}>
              <IconClose />
            </button>

            {!submitted && (
              <>
                <div className="ke-contact-steps">
                  <div className={`ke-contact-step-dot ${contactStep >= 1 ? "active" : ""}`} />
                  <div className={`ke-contact-step-dot ${contactStep >= 2 ? "active" : ""}`} />
                  <div className={`ke-contact-step-dot ${contactStep >= 3 ? "active" : ""}`} />
                </div>

                {contactStep === 1 && (
                  <div>
                    <h3 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#f0e8d8", marginBottom: 6, textAlign: "center" }}>
                      Express Your Interest
                    </h3>
                    <p style={{ fontSize: 13, color: "rgba(232,224,208,0.5)", textAlign: "center", marginBottom: 24 }}>
                      Select what you're looking for
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      {[
                        { icon: "🏠", label: "Buy/Sell" },
                        { icon: "🔑", label: "Rent" },
                        { icon: "📊", label: "Invest" },
                        { icon: "💰", label: "Mortgage" },
                        { icon: "🏗️", label: "Off-Plan" },
                        { icon: "📋", label: "General" },
                      ].map((opt) => (
                        <button
                          key={opt.label}
                          onClick={() => handleInterestSelect(opt.label)}
                          style={{
                            padding: "16px",
                            border: `1px solid ${contactInterest === opt.label ? "#C9A84C" : "rgba(201,168,76,0.14)"}`,
                            borderRadius: "8px",
                            background: contactInterest === opt.label ? "rgba(201,168,76,0.1)" : "transparent",
                            color: "#e8e0d0",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            textAlign: "center",
                          }}
                        >
                          <span style={{ fontSize: "28px", display: "block", marginBottom: "6px" }}>{opt.icon}</span>
                          <span style={{ fontSize: "12px", fontWeight: 600 }}>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {contactStep === 2 && (
                  <div>
                    <h3 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#f0e8d8", marginBottom: 6, textAlign: "center" }}>
                      How Many Bedrooms?
                    </h3>
                    <p style={{ fontSize: 13, color: "rgba(232,224,208,0.5)", textAlign: "center", marginBottom: 24 }}>
                      Select your preference for {contactInterest}
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: "8px" }}>
                      {["1", "2", "3", "4", "5+"].map((b) => (
                        <button
                          key={b}
                          onClick={() => handleBedSelect(b)}
                          style={{
                            padding: "14px",
                            border: `1px solid ${contactBeds === b ? "#C9A84C" : "rgba(201,168,76,0.14)"}`,
                            borderRadius: "8px",
                            background: contactBeds === b ? "rgba(201,168,76,0.1)" : "transparent",
                            color: "#e8e0d0",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            textAlign: "center",
                            fontSize: "18px",
                            fontWeight: 600,
                          }}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                    <button
                      style={{
                        background: "transparent",
                        color: "#C9A84C",
                        border: "1px solid rgba(201,168,76,0.3)",
                        padding: "12px 24px",
                        fontSize: "12px",
                        fontWeight: 600,
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        cursor: "pointer",
                        borderRadius: "6px",
                        width: "100%",
                        justifyContent: "center",
                        marginTop: "16px",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => setContactStep(1)}
                    >
                      ← Back
                    </button>
                  </div>
                )}

                {contactStep === 3 && (
                  <div>
                    <h3 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#f0e8d8", marginBottom: 6, textAlign: "center" }}>
                      Your Details
                    </h3>
                    <p style={{ fontSize: 13, color: "rgba(232,224,208,0.5)", textAlign: "center", marginBottom: 20 }}>
                      We'll reach out within 24 hours
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <input
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: `1px solid ${errors.firstName ? "#ff6b6b" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: "6px",
                          padding: "11px 14px",
                          fontSize: "13px",
                          color: "#e8e0d0",
                          fontFamily: "'DM Sans', sans-serif",
                          outline: "none",
                          transition: "border-color 0.2s",
                          width: "100%",
                        }}
                        placeholder="First Name*"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      />
                      <input
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: `1px solid ${errors.lastName ? "#ff6b6b" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: "6px",
                          padding: "11px 14px",
                          fontSize: "13px",
                          color: "#e8e0d0",
                          fontFamily: "'DM Sans', sans-serif",
                          outline: "none",
                          transition: "border-color 0.2s",
                          width: "100%",
                        }}
                        placeholder="Last Name*"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      />
                    </div>
                    <input
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: `1px solid ${errors.email ? "#ff6b6b" : "rgba(255,255,255,0.08)"}`,
                        borderRadius: "6px",
                        padding: "11px 14px",
                        fontSize: "13px",
                        color: "#e8e0d0",
                        fontFamily: "'DM Sans', sans-serif",
                        outline: "none",
                        transition: "border-color 0.2s",
                        width: "100%",
                        marginBottom: "10px",
                      }}
                      placeholder="Email Address*"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                    <input
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: `1px solid ${errors.phone ? "#ff6b6b" : "rgba(255,255,255,0.08)"}`,
                        borderRadius: "6px",
                        padding: "11px 14px",
                        fontSize: "13px",
                        color: "#e8e0d0",
                        fontFamily: "'DM Sans', sans-serif",
                        outline: "none",
                        transition: "border-color 0.2s",
                        width: "100%",
                        marginBottom: "10px",
                      }}
                      placeholder="Phone Number*"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                    <textarea
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "6px",
                        padding: "11px 14px",
                        fontSize: "13px",
                        color: "#e8e0d0",
                        fontFamily: "'DM Sans', sans-serif",
                        outline: "none",
                        transition: "border-color 0.2s",
                        width: "100%",
                        resize: "vertical",
                        marginBottom: "10px",
                      }}
                      placeholder="Your message (optional)..."
                      rows={2}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />
                    <div style={{ 
                      background: "rgba(201,168,76,0.08)", 
                      border: "1px solid rgba(201,168,76,0.14)", 
                      padding: "10px 14px", 
                      borderRadius: "6px",
                      fontSize: "13px",
                      color: "rgba(232,224,208,0.5)",
                      marginBottom: "10px"
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                        <span>Interest:</span>
                        <span style={{ color: "#C9A84C", fontWeight: 600 }}>{contactInterest}</span>
                      </div>
                      {contactBeds && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span>Bedrooms:</span>
                          <span style={{ color: "#C9A84C", fontWeight: 600 }}>{contactBeds}</span>
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                      <button
                        style={{
                          background: "#C9A84C",
                          color: "#07101e",
                          border: "none",
                          padding: "12px 24px",
                          fontSize: "12px",
                          fontWeight: 700,
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          borderRadius: "6px",
                          flex: 1,
                          justifyContent: "center",
                          opacity: isSubmitting ? 0.7 : 1,
                          transition: "all 0.3s ease",
                        }}
                        onClick={handleSubmitContact}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "SENDING..." : "SUBMIT"}
                      </button>
                      <button
                        style={{
                          background: "transparent",
                          color: "#C9A84C",
                          border: "1px solid rgba(201,168,76,0.3)",
                          padding: "12px 20px",
                          fontSize: "12px",
                          fontWeight: 600,
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          borderRadius: "6px",
                          transition: "all 0.3s ease",
                        }}
                        onClick={() => setContactStep(2)}
                      >
                        ← Back
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {submitted && (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <h3 style={{ fontFamily: "Georgia, serif", fontSize: 24, color: "#f0e8d8", marginBottom: 8 }}>
                  Thank You!
                </h3>
                <p style={{ fontSize: 14, color: "rgba(232,224,208,0.55)", lineHeight: 1.7 }}>
                  Our team will contact you within 24 hours to discuss your {contactInterest} requirements.
                </p>
                <button 
                  style={{
                    background: "#C9A84C",
                    color: "#07101e",
                    border: "none",
                    padding: "12px 28px",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    borderRadius: "6px",
                    marginTop: "24px",
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => {
                    setSubmitted(false);
                    setContactStep(1);
                    setContactInterest("");
                    setContactBeds("");
                    setFormData({ firstName: "", lastName: "", email: "", phone: "", message: "", interest: "", bedrooms: "" });
                    setErrors({});
                    setContactModalOpen(false);
                    document.body.style.overflow = "unset";
                  }}
                >
                  CLOSE
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TOAST NOTIFICATION ── */}
      {toast && (
        <div className="ke-toast" id="ke-toast">
          {toast}
        </div>
      )}
    </>
  );
}